/** Sub-components for the Antecessores vault page. Pure-presentational where
 *  possible; state + callbacks are passed via props. */
import { Link } from "react-router-dom";

export function RedactedBlock() {
  return (
    <div className="border border-[#333] p-6 bg-[#0a0a0a]">
      <div className="tiv-redact text-xl tracking-widest" style={{ padding: "0.5rem 1rem" }}>
        ████████████████████████████████████████████████████████
      </div>
      <div className="tiv-redact text-xl tracking-widest mt-2" style={{ padding: "0.5rem 1rem" }}>
        ████████████████████████████████████
      </div>
      <div className="tiv-redact text-xl tracking-widest mt-2" style={{ padding: "0.5rem 1rem" }}>
        ████████████████████████████████████████████████
      </div>
      <div className="mt-4 text-[0.65rem] tracking-[0.4em] uppercase text-[#721C1C]">
        [redacted by the archivist]
      </div>
    </div>
  );
}

export function BodyView({ body }) {
  if (body === "[REDACTED]" || !body) return <RedactedBlock />;
  return <div className="text-base leading-loose whitespace-pre-wrap">{body}</div>;
}

export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function VaultGate({
  pass, setPass, error, loading, onUnlock, onKeyDown,
}) {
  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="vault-gate">
      <div className="w-full max-w-md border border-[#333] p-8 sm:p-10 bg-[#0a0a0a] space-y-7">
        <div className="space-y-3">
          <div className="text-[0.6rem] tracking-[0.4em] uppercase text-[#A3A3A3]">
            Quattuor Antetempus · Sealed
          </div>
          <div className="flex items-center gap-3">
            <span className="tiv-stamp">OMEGA·VAULT</span>
            <span className="text-[0.55rem] tracking-[0.3em] uppercase text-[#555]">Δ before chronos</span>
          </div>
          <h1 className="font-serif-display text-3xl tracking-tighter">
            Antecessores
          </h1>
          <p className="text-xs text-[#555] leading-relaxed">
            Four predate the hour. Four were before the word for &ldquo;before.&rdquo;
            Their record is not for the surface. Speak the passphrase to break the wax.
          </p>
        </div>

        <div className="space-y-4" onKeyDown={onKeyDown}>
          <label className="block">
            <div className="text-[0.6rem] tracking-[0.35em] uppercase text-[#555] mb-1">
              Passphrase
            </div>
            <input
              type="password"
              className="tiv-input"
              value={pass}
              onChange={(e) => setPass(e.target.value)}
              autoFocus
              required
              data-testid="vault-passphrase-input"
            />
          </label>
          {error && (
            <div className="text-xs text-[#721C1C] border border-[#721C1C] p-2 tracking-wide" data-testid="vault-error">
              {error}
            </div>
          )}
          <button className="tiv-btn w-full" type="button" onClick={onUnlock} disabled={loading} data-testid="vault-unlock-btn">
            {loading ? "verifying..." : "Break the Wax"}
          </button>
        </div>

        <div className="text-[0.6rem] tracking-[0.35em] uppercase text-[#555] text-center pt-2 border-t border-[#333]">
          <Link to="/" className="hover:text-[#E3E1D9]">↩ Return to surface</Link>
        </div>
      </div>
    </div>
  );
}

function SigilColumn({ record, isEditing, draft, setDraft }) {
  return (
    <aside className="lg:col-span-3 space-y-3">
      <div className="font-serif-display text-7xl leading-none text-[#E3E1D9]">
        {(isEditing ? draft.sigil : record.sigil) || "○"}
      </div>
      <div className="text-[0.6rem] tracking-[0.4em] uppercase text-[#555]">
        {record.id} · Δ before chronos
      </div>
      {isEditing ? (
        <>
          <input
            className="tiv-input"
            value={draft.sigil}
            onChange={(e) => setDraft({ ...draft, sigil: e.target.value })}
            placeholder="sigil (single glyph)"
            data-testid={`antecessor-edit-sigil-${record.id}`}
          />
          <input
            className="tiv-input"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            placeholder="true name (unspoken)"
            data-testid={`antecessor-edit-name-${record.id}`}
          />
        </>
      ) : (
        <div className="text-xs text-[#A3A3A3]">
          <div className="text-[0.55rem] tracking-[0.3em] uppercase text-[#555] mb-1">True name</div>
          {record.name ? (
            <span className="tiv-redact">{record.name}</span>
          ) : (
            <span className="italic text-[#555]">[ unspoken ]</span>
          )}
        </div>
      )}
    </aside>
  );
}

function EpithetField({ record, isEditing, draft, setDraft }) {
  if (isEditing) {
    return (
      <input
        className="tiv-input text-2xl"
        value={draft.epithet}
        onChange={(e) => setDraft({ ...draft, epithet: e.target.value })}
        placeholder="epithet (e.g. The First Silence)"
        data-testid={`antecessor-edit-epithet-${record.id}`}
      />
    );
  }
  return (
    <h2 className="font-serif-display text-3xl sm:text-4xl tracking-tighter leading-tight">
      {record.epithet || <span className="text-[#555] italic">[ epithet pending ]</span>}
    </h2>
  );
}

function BodyField({ record, isEditing, draft, setDraft, view }) {
  return (
    <div>
      <div className="section-label">
        <span>Record · Body</span>
        <span>§ ante-horam</span>
      </div>
      {isEditing ? (
        <textarea
          className="tiv-textarea"
          value={draft.body}
          onChange={(e) => setDraft({ ...draft, body: e.target.value })}
          placeholder="the recovered fragment..."
          data-testid={`antecessor-edit-body-${record.id}`}
        />
      ) : (
        <BodyView body={view.body} />
      )}
    </div>
  );
}

function NotesField({ record, isEditing, draft, setDraft, view }) {
  return (
    <div>
      <div className="section-label">
        <span>Marginalia</span>
        <span>notes</span>
      </div>
      {isEditing ? (
        <textarea
          className="tiv-textarea"
          style={{ minHeight: "140px" }}
          value={draft.notes}
          onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
          placeholder="marginalia..."
          data-testid={`antecessor-edit-notes-${record.id}`}
        />
      ) : view.notes ? (
        <div className="text-sm font-mono-data text-[#A3A3A3] leading-loose whitespace-pre-wrap border-l-2 border-[#333] pl-4">
          {view.notes}
        </div>
      ) : (
        <div className="text-[#555] text-xs tracking-widest uppercase">::: no marginalia :::</div>
      )}
    </div>
  );
}

function PlatesField({ record, isEditing, view, onUpload, removeImage }) {
  return (
    <div>
      <div className="section-label">
        <span>Plates</span>
        <span>{(view.images || []).length}</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {(view.images || []).map((src, i) => (
          <div key={`${src.slice(-24)}-${i}`} className="border border-[#333] aspect-square overflow-hidden relative group">
            <img src={src} alt={`plate-${i}`} className="w-full h-full object-cover tiv-img" />
            {isEditing && (
              <button
                onClick={() => removeImage(i)}
                className="absolute top-1 right-1 tiv-btn tiv-btn-danger text-[0.55rem] px-2 py-1 opacity-0 group-hover:opacity-100"
              >
                Burn
              </button>
            )}
          </div>
        ))}
        {isEditing && (
          <label className="border border-dashed border-[#333] aspect-square flex items-center justify-center cursor-crosshair hover:border-[#E3E1D9]" data-testid={`antecessor-img-upload-${record.id}`}>
            <input type="file" accept="image/*" className="hidden" onChange={onUpload} />
            <span className="text-xs tracking-[0.3em] uppercase text-[#A3A3A3]">+ Affix Plate</span>
          </label>
        )}
      </div>
    </div>
  );
}

function ActionRow({ record, isEditing, onSave, onCancel, onEdit }) {
  return (
    <div className="flex gap-3 pt-4">
      {isEditing ? (
        <>
          <button className="tiv-btn" onClick={onSave} data-testid={`antecessor-save-${record.id}`}>
            Seal & Save
          </button>
          <button className="tiv-btn" onClick={onCancel} data-testid={`antecessor-cancel-${record.id}`}>
            Discard
          </button>
        </>
      ) : (
        <button className="tiv-btn" onClick={() => onEdit(record)} data-testid={`antecessor-edit-${record.id}`}>
          Inscribe / Edit
        </button>
      )}
    </div>
  );
}

export function AntecessorArticle({
  record, editingId, draft, setDraft, isAdmin,
  onEdit, onSave, onCancel, onUpload, removeImage,
}) {
  const isEditing = editingId === record.id;
  const view = isEditing ? draft : record;
  return (
    <article
      className="grid grid-cols-1 lg:grid-cols-12 gap-8 border-t border-[#333] pt-10"
      data-testid={`antecessor-${record.id}`}
    >
      <SigilColumn record={record} isEditing={isEditing} draft={draft} setDraft={setDraft} />
      <div className="lg:col-span-9 space-y-5">
        <EpithetField record={record} isEditing={isEditing} draft={draft} setDraft={setDraft} />
        <BodyField record={record} isEditing={isEditing} draft={draft} setDraft={setDraft} view={view} />
        <NotesField record={record} isEditing={isEditing} draft={draft} setDraft={setDraft} view={view} />
        <PlatesField record={record} isEditing={isEditing} view={view} onUpload={onUpload} removeImage={removeImage} />
        {isAdmin && (
          <ActionRow
            record={record}
            isEditing={isEditing}
            onSave={onSave}
            onCancel={onCancel}
            onEdit={onEdit}
          />
        )}
      </div>
    </article>
  );
}
